function splitObjectTypeWords(objectType: string): string {
  return objectType.replace(/([A-Z])/g, ' $1').trim();
}

export function getTitleCasedObjectTypeLabel(objectType: string): string {
  const words = splitObjectTypeWords(objectType);
  if (words === '') {
    return '';
  }
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

// Initialisms (e.g. "RNA") are read letter-by-letter, so the article depends on how the
// first letter's *name* sounds, not its spelling. These letters' names start with a vowel
// sound (e.g. "R" → "are", "F" → "eff"), so an initialism beginning with one takes "an".
const VOWEL_SOUND_INITIAL_LETTERS = new Set(['A', 'E', 'F', 'H', 'I', 'L', 'M', 'N', 'O', 'R', 'S', 'X']);

export function withIndefiniteArticle(noun: string): string {
  const firstWord = noun.split(' ')[0] ?? '';
  const isInitialism = firstWord.length > 1 && /^[A-Z]+$/.test(firstWord);
  const startsWithVowelSound = isInitialism
    ? VOWEL_SOUND_INITIAL_LETTERS.has(firstWord.charAt(0))
    : /^[aeiou]/i.test(noun);
  return startsWithVowelSound ? `an ${noun}` : `a ${noun}`;
}

export function toCommonNounLabel(label: string): string {
  return label
    .split(' ')
    .map((word) => (word === word.toUpperCase() ? word : word.toLowerCase()))
    .join(' ');
}

const DEFAULT_RUN_TIME = {hour12: 8, minute: 0, period: 'AM' as const};

// The 24-hour HH:MM value a new time-based routine starts at. Shared so the form's fallback
// and the reducer's default config can't drift apart.
export const DEFAULT_RUN_TIME_STRING = '08:00';

export function parseRunTime(runTime: string): {hour12: number; minute: number; period: 'AM' | 'PM'} {
  const match = /^(\d{1,2}):(\d{2})$/.exec(runTime ?? '');
  if (match == null) {
    return {...DEFAULT_RUN_TIME};
  }
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  if (hour24 < 0 || hour24 > 23 || minute < 0 || minute > 59) {
    return {...DEFAULT_RUN_TIME};
  }
  const period = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return {hour12, minute, period};
}

export function buildRunTime(hour12: number, minute: number, period: 'AM' | 'PM'): string {
  // Clamp both parts so a transient out-of-range value (e.g. Mantine NumberInput reporting an
  // unclamped value mid-typing, before it clamps on blur) can never produce a run_time that
  // fails the backend's strict HH:MM validation.
  const clampedHour12 = Math.min(12, Math.max(1, Math.round(hour12) || 12));
  const clampedMinute = Math.min(59, Math.max(0, Math.round(minute) || 0));
  const normalizedHour12 = clampedHour12 % 12;
  const hour24 = period === 'PM' ? normalizedHour12 + 12 : normalizedHour12;
  return `${String(hour24).padStart(2, '0')}:${String(clampedMinute).padStart(2, '0')}`;
}

export function formatRunTimeLabel(runTime: string): string {
  const {hour12, minute, period} = parseRunTime(runTime);
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

function randomIntInclusive(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

// Weekly time-based presets pick a random weekday (Mon–Fri) and hour (7am–7pm, on the hour) so
// that users starting from the same example don't all schedule their routine at the same time,
// which would otherwise concentrate scheduled load. For these presets we choose weekday values
// 1–5 (Mon–Fri); the broader config supports 0–6 (Sun–Sat). run_time is a 24-hour HH:MM string.
export function getRandomWeeklyScheduleDefaults(): {weekly_days: Array<number>; run_time: string} {
  const weekday = randomIntInclusive(1, 5);
  const hour24 = randomIntInclusive(7, 19);
  return {
    weekly_days: [weekday],
    run_time: `${String(hour24).padStart(2, '0')}:00`,
  };
}

// Renders an IANA timezone (e.g. "America/Los_Angeles") as a short abbreviation (e.g. "PDT"),
// for read-only display next to the run time. Falls back to the raw timezone string if the
// environment can't resolve an abbreviation.
export function formatTimezoneAbbreviation(timezone: string): string {
  if (!timezone) {
    return '';
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts();
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? timezone;
  } catch (err) {
    // Intl throws a RangeError for an unrecognized timezone; fall back to the raw string.
    // Anything else is unexpected and should surface.
    if (err instanceof RangeError) {
      return timezone;
    }
    throw err;
  }
}
